import React, { useRef, useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useTheme } from 'styled-components';
import { calculatePosition, isOutsideProtectiveZone, PositionType } from '../../../utils/calculate-position';
import { PaletteColorType } from '../../../theme/type/palette-type';
import { cn } from '../../../utils/tailwind';
import styles from './floating-object.module.css';
import { calculateContainedPosition } from '../../../utils/calculate-contained-position';
import { fitTooltipText } from '../../../utils/fit-tooltip-text';

type FloatingObjectType = {
    trigger: React.ReactNode;
    content: React.ReactNode;
    position?: PositionType;
    isVisible: boolean;
    setIsVisible?: (visible: boolean) => void;
    arrowPosition?: number;
    size?: 'small' | 'medium' | 'large';
    cursor?: boolean;
    block?: boolean;
    icon?: React.ReactNode | null;
    contentWrapper?: React.ReactNode;
    arrowColor?: PaletteColorType;
    arrowColorOverride?: string;
    animateEntrance?: boolean;
    constrainToDialog?: boolean;
    protectiveZone?: number;
    onClickOutside?: () => void;
};

const getTransformByPosition = (position: PositionType, size: 'small' | 'medium' | 'large', hasIcon: boolean) => {
    const verticalOffset = size === 'small' ? '1rem' : '1.6rem';
    let horizontalOffset = '0rem';

    if (hasIcon) {
        horizontalOffset = size === 'small' ? '1rem' : '1.6rem';
    }

    switch (position) {
        case 'top':
            return `translateY(-${verticalOffset})`;
        case 'bottom':
            return `translateY(${verticalOffset})`;
        case 'top-left':
            return `translate(-${horizontalOffset}, -${verticalOffset})`;
        case 'top-right':
            return `translate(${horizontalOffset}, -${verticalOffset})`;
        case 'bottom-left':
            return `translate(-${horizontalOffset}, ${verticalOffset})`;
        case 'bottom-right':
            return `translate(${horizontalOffset}, ${verticalOffset})`;
        default:
            return undefined;
    }
};

const getArrowStyle = (
    position: PositionType,
    size: 'small' | 'medium' | 'large',
    arrowPosition: number | undefined,
    arrowColor: string,
) => {
    let arrowOffset = `${arrowPosition}rem`;

    if (!arrowPosition) {
        arrowOffset = size === 'small' ? '1.2rem' : '1.6rem';
    }
    const borderWidth = size === 'small' ? '0.5rem' : '0.8rem';
    const baseStyle: React.CSSProperties = {
        borderWidth,
        borderStyle: 'solid',
        borderColor: 'transparent',
    };

    switch (position) {
        case 'bottom':
            return {
                ...baseStyle,
                left: '50%',
                transform: 'translateX(-50%)',
                bottom: '100%',
                borderBottomColor: arrowColor,
            } as React.CSSProperties;
        case 'top':
            return {
                ...baseStyle,
                left: '50%',
                transform: 'translateX(-50%)',
                top: '100%',
                borderTopColor: arrowColor,
            } as React.CSSProperties;
        case 'top-left':
            return {
                ...baseStyle,
                top: '100%',
                left: arrowOffset,
                borderTopColor: arrowColor,
            } as React.CSSProperties;
        case 'top-right':
            return {
                ...baseStyle,
                top: '100%',
                right: arrowOffset,
                borderTopColor: arrowColor,
            } as React.CSSProperties;
        case 'bottom-right':
            return {
                ...baseStyle,
                bottom: '100%',
                right: arrowOffset,
                borderBottomColor: arrowColor,
            } as React.CSSProperties;
        case 'bottom-left':
            return {
                ...baseStyle,
                bottom: '100%',
                left: arrowOffset,
                borderBottomColor: arrowColor,
            } as React.CSSProperties;
        default:
            return baseStyle;
    }
};

export const FloatingObject: React.FC<FloatingObjectType> = ({
    trigger,
    content,
    position = 'bottom',
    isVisible,
    setIsVisible,
    arrowPosition,
    size = 'medium',
    block = false,
    cursor = false,
    icon,
    arrowColor,
    arrowColorOverride,
    animateEntrance = false,
    constrainToDialog = false,
    protectiveZone = 0,
    onClickOutside,
}) => {
    const theme = useTheme();
    const [floatingPosition, setFloatingPosition] = useState<{
        top: number;
        left: number;
        adjustedPosition: PositionType;
        arrowLeft?: number;
    } | null>(null);

    const triggerRef = useRef<HTMLDivElement>(null);
    const floatingRef = useRef<HTMLDivElement>(null);

    const calculateFloatingPosition = useCallback(() => {
        if (floatingRef.current && triggerRef.current) {
            const triggerRect = triggerRef.current.getBoundingClientRect();
            if (constrainToDialog) {
                const dialog = triggerRef.current.closest('[role="dialog"]')?.getBoundingClientRect();
                const bounds = {
                    left: Math.max(0, dialog?.left ?? 0) + 12,
                    right: Math.min(window.innerWidth, dialog?.right ?? window.innerWidth) - 12,
                    top: Math.max(0, dialog?.top ?? 0) + 12,
                    bottom: Math.min(window.innerHeight, dialog?.bottom ?? window.innerHeight) - 12,
                };
                floatingRef.current.style.setProperty('--floating-max-width', `${bounds.right - bounds.left}px`);
                floatingRef.current.style.setProperty('--floating-max-height', `${bounds.bottom - bounds.top}px`);
                const textTooltip = floatingRef.current.querySelector<HTMLElement>('[data-fit-tooltip-text]');
                if (textTooltip) fitTooltipText(textTooltip);
                const contained = calculateContainedPosition(
                    position,
                    triggerRect,
                    floatingRef.current.getBoundingClientRect(),
                    bounds,
                    size === 'small' ? 10 : 16,
                );
                setFloatingPosition({
                    ...contained,
                    top: contained.top + window.scrollY,
                    left: contained.left + window.scrollX,
                });
                return;
            }
            const floatingRect = floatingRef.current.getBoundingClientRect();

            const { top, left, adjustedPosition } = calculatePosition(position, triggerRect, floatingRect);

            setFloatingPosition({ top, left, adjustedPosition });
        }
    }, [position, constrainToDialog, size]);

    // If hover action
    if (setIsVisible) {
        const handleMouseLeave = useCallback(
            (event: MouseEvent) => {
                if (triggerRef.current && floatingRef.current) {
                    const triggerRect = triggerRef.current.getBoundingClientRect();
                    const floatingRect = floatingRef.current.getBoundingClientRect();

                    if (
                        isOutsideProtectiveZone(event.clientX, event.clientY, triggerRect, floatingRect, protectiveZone)
                    ) {
                        setIsVisible(false);
                    }
                }
            },
            [setIsVisible],
        );

        useEffect(() => {
            if (isVisible) {
                calculateFloatingPosition();
            }
        }, [isVisible, calculateFloatingPosition]);

        useEffect(() => {
            if (isVisible) {
                document.addEventListener('mousemove', handleMouseLeave);
            } else {
                document.removeEventListener('mousemove', handleMouseLeave);
            }

            return () => {
                document.removeEventListener('mousemove', handleMouseLeave);
            };
        }, [isVisible, handleMouseLeave]);
    }

    useEffect(() => {
        if (!isVisible || !onClickOutside) return undefined;

        const handleClickOutside = (event: MouseEvent) => {
            const target = event.target as Node;
            if (
                floatingRef.current &&
                !floatingRef.current.contains(target) &&
                triggerRef.current &&
                !triggerRef.current.contains(target)
            ) {
                onClickOutside();
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isVisible, onClickOutside]);

    useEffect(() => {
        if (isVisible) {
            calculateFloatingPosition();

            const handleResize = () => {
                calculateFloatingPosition();
            };

            window.addEventListener('resize', handleResize);
            if (constrainToDialog) window.addEventListener('scroll', handleResize, true);

            return () => {
                window.removeEventListener('resize', handleResize);
                if (constrainToDialog) window.removeEventListener('scroll', handleResize, true);
            };
        }
        return undefined;
    }, [isVisible, calculateFloatingPosition, constrainToDialog]);

    const adjustedPosition = floatingPosition?.adjustedPosition || position;
    const resolvedArrowColor =
        arrowColorOverride ?? (arrowColor ? theme.palette.color[arrowColor].main : theme.palette.background.header);
    const floatingContent = (
        <>
            <span
                className="pointer-events-none absolute h-0 w-0"
                style={{
                    ...getArrowStyle(adjustedPosition, size, arrowPosition, resolvedArrowColor),
                    ...(constrainToDialog && floatingPosition
                        ? {
                              left: floatingPosition.arrowLeft,
                              right: undefined,
                              transform: 'translateX(-50%)',
                          }
                        : {}),
                }}
            />
            {content}
        </>
    );

    return (
        <>
            <div
                ref={triggerRef}
                onMouseEnter={setIsVisible ? () => setIsVisible(true) : undefined}
                onFocus={constrainToDialog && setIsVisible ? () => setIsVisible(true) : undefined}
                onBlur={constrainToDialog && setIsVisible ? () => setIsVisible(false) : undefined}
                className={cn(
                    'relative items-center gap-[1rem]',
                    block ? 'flex' : 'inline-flex',
                    cursor && 'cursor-help',
                )}
            >
                {trigger}
                {icon && <div className="inline-flex">{icon}</div>}
            </div>
            {isVisible
                ? createPortal(
                      <div
                          ref={floatingRef}
                          className="absolute z-[9999]"
                          style={{
                              transform: constrainToDialog
                                  ? undefined
                                  : getTransformByPosition(adjustedPosition, size, !!icon),
                              top: floatingPosition?.top,
                              left: floatingPosition?.left,
                              visibility: floatingPosition ? 'visible' : 'hidden',
                          }}
                      >
                          {animateEntrance ? (
                              <div className={styles.enter} data-position={adjustedPosition}>
                                  {floatingContent}
                              </div>
                          ) : (
                              floatingContent
                          )}
                      </div>,
                      document.body,
                  )
                : null}
        </>
    );
};
